import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BankPicker } from '@/components/BankPicker'
import { t } from '@/i18n'
import {
  createAccount,
  deleteAccount,
  deleteAccountCredentials,
  getAccounts,
  getExchanges,
  getMe,
  patchAccount,
  putAccountCredentials,
  syncAccount,
  type AccountOut,
} from '@/lib/api'
import { formatMoney, parseAmount } from '@/lib/format'

const KIND_IDS = ['exchange', 'bank', 'wallet', 'cash'] as const

function kindLabel(kind: string): string {
  const key = `acc.${kind}`
  const translated = t(key)
  return translated === key ? kind : translated
}

export function Accounts() {
  const queryClient = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const exchanges = useQuery({ queryKey: ['exchanges'], queryFn: getExchanges })
  const canDelete = ['owner', 'admin', 'operator'].includes(me.data?.organization.role ?? '')
  const [kind, setKind] = useState<(typeof KIND_IDS)[number]>('bank')
  const [name, setName] = useState('')
  const [currency, setCurrency] = useState('PLN')
  const [opening, setOpening] = useState('0')
  const [exchange, setExchange] = useState('binance')
  const [bankId, setBankId] = useState<string | null>(null)
  const [dailyLimit, setDailyLimit] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      createAccount({
        kind,
        name,
        currency_code: currency,
        opening_balance: opening,
        exchange_code: kind === 'exchange' ? exchange : null,
        bank_id: kind === 'bank' ? bankId : null,
        daily_limit: kind === 'bank' ? parseAmount(dailyLimit) || null : null,
      }),
    onSuccess: () => {
      setName('')
      setDailyLimit('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : t('acc.createFail')),
  })

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('acc.title')}</h1>
      <p className="mt-1 text-sm text-slate-500">{t('acc.lead')}</p>

      <form
        className="mt-6 grid gap-3 rounded-lg border border-surface-border bg-surface-raised p-5 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault()
          create.mutate()
        }}
      >
        <label className="text-xs text-slate-400">
          {t('acc.type')}
          <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)} className={inputClass}>
            {KIND_IDS.map((id) => (
              <option key={id} value={id}>
                {kindLabel(id)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-400">
          {t('acc.name')}
          <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} required />
        </label>
        <label className="text-xs text-slate-400">
          {t('acc.ccy')}
          <input value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} className={inputClass} />
        </label>
        <label className="text-xs text-slate-400">
          {t('acc.open')}
          <input value={opening} onChange={(event) => setOpening(event.target.value)} className={`${inputClass} num`} />
        </label>
        {kind === 'exchange' && (
          <label className="text-xs text-slate-400">
            {t('acc.exchange')}
            <select value={exchange} onChange={(event) => setExchange(event.target.value)} className={inputClass}>
              {(exchanges.data ?? []).map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {kind === 'bank' && (
          <>
            <div className="text-xs text-slate-400">
              {t('acc.bank')}
              <div className="mt-1">
                <BankPicker fiat={currency} value={bankId} onChange={(id) => setBankId(id)} />
              </div>
            </div>
            <label className="text-xs text-slate-400">
              {t('acc.dayLimit')}
              <input
                value={dailyLimit}
                onChange={(event) => setDailyLimit(event.target.value)}
                className={`${inputClass} num`}
                placeholder={t('acc.hidePh')}
                inputMode="decimal"
              />
            </label>
          </>
        )}
        {error && <p className="text-sm text-loss sm:col-span-2">{error}</p>}
        <div className="sm:col-span-2">
          <button type="submit" className="rounded-md bg-slate-100 px-4 py-2 text-sm text-slate-900">
            {t('acc.add')}
          </button>
        </div>
      </form>

      <table className="mt-6 w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="py-2">{t('acc.name')}</th>
            <th className="py-2">{t('acc.type')}</th>
            <th className="py-2">{t('acc.ccy')}</th>
            <th className="py-2">{t('acc.limit')}</th>
            <th className="py-2">{t('acc.key')}</th>
            <th className="py-2 text-right">{t('acc.open')}</th>
            <th className="py-2 text-right"> </th>
          </tr>
        </thead>
        <tbody>
          {(accounts.data ?? []).map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              canDelete={canDelete}
              open={openId === account.id}
              onToggle={() => setOpenId((current) => (current === account.id ? null : account.id))}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AccountRow({
  account,
  canDelete,
  open,
  onToggle,
}: {
  account: AccountOut
  canDelete: boolean
  open: boolean
  onToggle: () => void
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remove = useMutation({
    mutationFn: () => deleteAccount(account.id),
    onSuccess: () => {
      setConfirming(false)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : t('acc.deleteFail')),
  })
  const canConnect = Boolean(account.connector)
  const isBank = account.kind === 'bank'
  const expandable = canConnect || isBank
  return (
    <>
      <tr className="border-t border-surface-border/60">
        <td className="py-2">
          {expandable ? (
            <button type="button" className="text-left text-slate-100 hover:underline" onClick={onToggle}>
              {account.name}
            </button>
          ) : (
            account.name
          )}
        </td>
        <td className="py-2 text-slate-400">{kindLabel(account.kind)}</td>
        <td className="py-2">{account.currency_code ?? '—'}</td>
        <td className="num py-2">
          {isBank && account.daily_limit
            ? formatMoney(account.daily_limit, account.currency_code ?? '')
            : isBank
              ? '—'
              : ''}
        </td>
        <td className="py-2 text-xs">
          {account.has_credentials ? (
            <span className="text-profit">{t('acc.has')}</span>
          ) : canConnect ? (
            <span className="text-slate-600">{t('acc.no')}</span>
          ) : (
            <span className="text-slate-700">—</span>
          )}
        </td>
        <td className="num py-2 text-right">
          {formatMoney(account.opening_balance, account.currency_code ?? '')}
        </td>
        <td className="py-2 text-right">
          {canDelete &&
            (confirming ? (
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className="min-h-11 rounded-md border border-surface-border px-3 text-xs text-slate-300"
                  onClick={() => {
                    setConfirming(false)
                    setError(null)
                    remove.reset()
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="min-h-11 rounded-md bg-loss px-3 text-xs text-white disabled:opacity-50"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {t('acc.deleteAsk')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="min-h-11 rounded-md border border-loss/40 px-3 text-xs text-loss"
                onClick={() => setConfirming(true)}
              >
                {t('acc.delete')}
              </button>
            ))}
          {error && <p className="mt-1 text-xs text-loss">{error}</p>}
        </td>
      </tr>
      {open && expandable && (
        <tr>
          <td colSpan={7} className="pb-4">
            {isBank && <LimitPanel account={account} />}
            {canConnect && <CredentialPanel account={account} />}
          </td>
        </tr>
      )}
    </>
  )
}

function LimitPanel({ account }: { account: AccountOut }) {
  const queryClient = useQueryClient()
  const [limit, setLimit] = useState(account.daily_limit ? String(Number(account.daily_limit)) : '')
  const [used, setUsed] = useState(
    account.daily_used_override ? String(Number(account.daily_used_override)) : '',
  )
  const [message, setMessage] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () =>
      patchAccount(account.id, {
        daily_limit: parseAmount(limit) || null,
        daily_used_override: parseAmount(used) || null,
      }),
    onSuccess: () => {
      setMessage(t('acc.limitSaved'))
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (err: unknown) => setMessage(err instanceof Error ? err.message : t('acc.notSaved')),
  })
  return (
    <form
      className="mb-3 flex flex-wrap items-end gap-3 rounded-md border border-surface-border bg-surface p-4 text-xs text-slate-400"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <label>
        {t('acc.dayLimitShort')}
        <input
          value={limit}
          onChange={(event) => setLimit(event.target.value)}
          className={`${inputClass} w-36 num`}
          placeholder={t('acc.limitHide')}
          inputMode="decimal"
        />
      </label>
      <label>
        {t('acc.usedToday')}
        <input
          value={used}
          onChange={(event) => setUsed(event.target.value)}
          className={`${inputClass} w-36 num`}
          placeholder={t('acc.usedAuto')}
          inputMode="decimal"
        />
      </label>
      <button type="submit" className="rounded-md bg-slate-100 px-3 py-1.5 text-slate-900">
        {t('acc.saveLimit')}
      </button>
      {message && <p className="w-full text-slate-300">{message}</p>}
    </form>
  )
}

function CredentialPanel({ account }: { account: AccountOut }) {
  const queryClient = useQueryClient()
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [token, setToken] = useState('')
  const [merchantId, setMerchantId] = useState('')
  const [password, setPassword] = useState('')
  const [monoAccount, setMonoAccount] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => {
      if (account.connector === 'monobank') return putAccountCredentials(account.id, { token, account_id: monoAccount || undefined })
      if (account.connector === 'privat24') return putAccountCredentials(account.id, { merchant_id: merchantId, password })
      if (account.connector === 'openbanking') return putAccountCredentials(account.id, { access_token: token })
      return putAccountCredentials(account.id, { api_key: apiKey, api_secret: apiSecret })
    },
    onSuccess: () => {
      setApiKey('')
      setApiSecret('')
      setToken('')
      setMerchantId('')
      setPassword('')
      setMessage(t('acc.keySaved'))
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onError: (err: unknown) => setMessage(err instanceof Error ? err.message : t('acc.notSaved')),
  })
  const remove = useMutation({
    mutationFn: () => deleteAccountCredentials(account.id),
    onSuccess: () => {
      setMessage(t('acc.keyCleared'))
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onError: (err: unknown) => setMessage(err instanceof Error ? err.message : t('acc.clearedFail')),
  })
  const sync = useMutation({
    mutationFn: () => syncAccount(account.id),
    onSuccess: (result) => {
      if (result.skipped) setMessage(result.reason ?? t('acc.skipped'))
      else if (result.ok) setMessage(t('acc.synced', { n: result.stored }))
      else setMessage(result.error ?? t('acc.syncFail'))
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onError: (err: unknown) => setMessage(err instanceof Error ? err.message : t('acc.syncErr')),
  })

  return (
    <div className="rounded-md border border-surface-border bg-surface p-4 text-xs text-slate-400">
      <p>
        {t('acc.connector')} <span className="text-slate-200">{account.connector}</span>
        {account.sync_status ? t('acc.syncState', { s: account.sync_status }) : ''}
      </p>
      {account.last_error && <p className="mt-1 text-loss">{account.last_error}</p>}
      <form
        className="mt-3 grid gap-2 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        {(account.connector === 'binance_p2p' || account.connector === 'bybit_p2p') && (
          <>
            <input
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              className={inputClass}
              placeholder={t('acc.apiKeyRo')}
              autoComplete="off"
            />
            <input
              value={apiSecret}
              onChange={(event) => setApiSecret(event.target.value)}
              className={inputClass}
              placeholder="API secret"
              type="password"
              autoComplete="off"
            />
          </>
        )}
        {account.connector === 'monobank' && (
          <>
            <input
              value={token}
              onChange={(event) => setToken(event.target.value)}
              className={inputClass}
              placeholder={t('acc.monoToken')}
              type="password"
              autoComplete="off"
            />
            <input
              value={monoAccount}
              onChange={(event) => setMonoAccount(event.target.value)}
              className={inputClass}
              placeholder={t('acc.monoAcc')}
              autoComplete="off"
            />
          </>
        )}
        {account.connector === 'privat24' && (
          <>
            <input
              value={merchantId}
              onChange={(event) => setMerchantId(event.target.value)}
              className={inputClass}
              placeholder="merchant_id"
              autoComplete="off"
            />
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={inputClass}
              placeholder="password"
              type="password"
              autoComplete="off"
            />
          </>
        )}
        {account.connector === 'openbanking' && (
          <input
            value={token}
            onChange={(event) => setToken(event.target.value)}
            className={`${inputClass} sm:col-span-2`}
            placeholder={t('acc.aggregator')}
            type="password"
            autoComplete="off"
          />
        )}
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <button type="submit" className="rounded-md bg-slate-100 px-3 py-1.5 text-slate-900">
            {t('acc.saveKey')}
          </button>
          <button
            type="button"
            className="rounded-md border border-surface-border px-3 py-1.5 text-slate-200"
            onClick={() => sync.mutate()}
            disabled={!account.has_credentials}
          >
            {t('acc.syncNow')}
          </button>
          {account.has_credentials && (
            <button type="button" className="rounded-md px-3 py-1.5 text-loss" onClick={() => remove.mutate()}>
              {t('acc.clearKey')}
            </button>
          )}
        </div>
      </form>
      {message && <p className="mt-2 text-slate-300">{message}</p>}
    </div>
  )
}

const inputClass = 'mt-1 w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-sm text-slate-100'
